import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { getIntercomWebJwt } from '~/services/users/users.service.api';
import isWebView from '~/utils/isWebView';

const REFRESH_MARGIN_MS = 10 * 60 * 1000;
const MIN_REFRESH_MS = 60 * 1000;

/** Milliseconds until the JWT should be refreshed: 10 minutes before `expiresAt` (unix seconds), at least 1 minute. */
export const msUntilRefresh = (expiresAt: number, now: number = Date.now()): number =>
  Math.max(expiresAt * 1000 - now - REFRESH_MARGIN_MS, MIN_REFRESH_MS);

export function useIntercomJwt(): { jwt: string | undefined; isLoading: boolean } {
  const { user } = useContext(UserContext);
  const enabled = !!user?.id && !isWebView();

  const query = useQuery({
    queryKey: ['intercomJwt', user?.id],
    queryFn: getIntercomWebJwt,
    enabled,
    staleTime: Infinity,
    refetchInterval: (data) => (data ? msUntilRefresh(data.expiresAt) : false),
    refetchIntervalInBackground: true,
  });

  // React Query v4 reports disabled queries as loading; only a real in-flight fetch counts.
  return { jwt: query.data?.jwt, isLoading: enabled && query.isLoading };
}
